// src/AppLayout.tsx

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  CssBaseline,
  Menu,
  MenuItem,
  Button,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import BarChartIcon from '@mui/icons-material/BarChart';
import UpdateIcon from '@mui/icons-material/Update';
import LogoutIcon from '@mui/icons-material/Logout';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InsightsIcon from '@mui/icons-material/Insights';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DescriptionIcon from '@mui/icons-material/Description';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HistoryIcon from '@mui/icons-material/History';
import { useThemeContext } from './components/theme/ThemeContext';
import { Dashboard, Engineering, FindInPage, Settings as SettingsIcon, ManageAccounts as ManageAccountsIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import NotificacaoIcone from './components/NotificacaoIcone';
import { NotificacaoProvider } from './context/NotificacaoContext';
import { useAuth } from './context/AuthContext';
import ProtectedMenuItem from './components/ProtectedMenuItem';
import { UsuarioPerfil } from './types/enums/UsuarioPerfil';
import AiChatWidget from './components/AiChatWidget';

interface AppLayoutProps {
  isAuthenticated: boolean;
}

const drawerWidth = 240;

const AppLayout: React.FC<AppLayoutProps> = ({ isAuthenticated }) => {
  const [companyAnchorEl, setCompanyAnchorEl] = useState<null | HTMLElement>(null);
  const { toggleTheme, mode } = useThemeContext();
  const { signOut, tenants, selectedTenant, selectTenant, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);

  // Close drawer when switching to mobile
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleCompanySwitcher = async (tenantId: string) => {
    setCompanyAnchorEl(null);
    if (tenantId !== selectedTenant?.tenantId) {
      await selectTenant(tenantId);
      window.location.reload();
    }
  };

  const handleLogout = () => {
    signOut();
  };

  if (!isAuthenticated) return <Outlet />;

  const activeStyleLogout = (path: string) => ({
    backgroundColor: location.pathname === path ? theme.palette.primary.main : 'inherit',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      opacity: location.pathname === path ? 1 : 0.4,
      '& .MuiTypography-root': {
        color: theme.palette.primary.contrastText,
        fontWeight: 500,
      },
      '& .MuiListItemIcon-root svg': {
        color: '#fff',
      }
    },
    '& .MuiTypography-root': {
      color: location.pathname === path ? theme.palette.primary.contrastText : 'inherit',
      fontWeight: location.pathname === path ? 500 : 400,
    },
    '& .MuiListItemIcon-root svg': {
      color: location.pathname === path ? theme.palette.primary.contrastText : theme.palette.text.primary,
    }
  });

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* TopBar com tema e ajuste de posição responsivo */}
      <AppBar
        position="fixed"
        sx={{
          width: isMobile ? '100%' : (open ? `calc(100% - ${drawerWidth}px)` : '100%'),
          ml: isMobile ? 0 : (open ? `${drawerWidth}px` : 0),
          transition: 'width 0.3s, margin-left 0.3s',
          borderRadius: '0'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => setOpen(!open)}
            sx={{ mr: 2 }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <img src='/logo512teste.png' width="50px" alt="Logo Think Way" />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: '20px', display: { xs: 'none', sm: 'block' } }}>
            Think Way
          </Typography>
          <Box sx={{ flexGrow: { xs: 1, sm: 0 } }} />

          {/* Company Switcher — só mostra se tem mais de 1 tenant */}
          {tenants.length > 1 && (
            <>
              <Button
                color="inherit"
                onClick={(e) => setCompanyAnchorEl(e.currentTarget)}
                startIcon={<SwapHorizIcon />}
                sx={{
                  mr: 1,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  maxWidth: { xs: 120, sm: 220 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  px: { xs: 0.5, sm: 1 },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {selectedTenant?.empresaNome || 'Empresa'}
                </Box>
              </Button>
              <Menu
                anchorEl={companyAnchorEl}
                open={Boolean(companyAnchorEl)}
                onClose={() => setCompanyAnchorEl(null)}
              >
                <MenuItem disabled sx={{ opacity: 0.7, fontSize: '0.8rem' }}>
                  Trocar empresa
                </MenuItem>
                <Divider />
                {tenants.map((tenant) => (
                  <MenuItem
                    key={tenant.tenantId}
                    onClick={() => handleCompanySwitcher(tenant.tenantId)}
                    selected={tenant.tenantId === selectedTenant?.tenantId}
                  >
                    {tenant.empresaNome}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* Ícone de notificações */}
          <NotificacaoIcone />

          {/* Ícone de perfil */}
          <IconButton 
            color="inherit" 
            onClick={() => navigate('/tw/profile')}
            sx={{ mx: 1 }}
          >
            <AccountCircleIcon />
          </IconButton>

          <IconButton color="inherit" onClick={toggleTheme}>
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : (open ? drawerWidth : 60),
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? drawerWidth : (open ? drawerWidth : 60),
            boxSizing: 'border-box',
            transition: 'width 0.3s',
            borderRadius: '0'
          },
        }}
      >
        {open && (
          <Typography variant="h6" component="div" sx={{ m: 2 }}>
            Menu
          </Typography>
        )}
        <List sx={{ flexGrow: 1 }}>
          <ListItem onClick={() => setOpen(!open)} sx={{ textDecoration: 'none', display: open ? 'none' : 'block' }} >
            <ListItemIcon>
              <MenuIcon />
            </ListItemIcon>
          </ListItem>

          <ProtectedMenuItem tela="dashboard" rota="/tw/dashboard" icon={<Dashboard color='secondary' />} label="Dashboard" drawerOpen={open} />
          <ProtectedMenuItem tela="diretrizes" rota="/tw/diretrizes" icon={<FactCheckIcon color="secondary" />} label="Diretrizes" drawerOpen={open} />
          <ProtectedMenuItem tela="colaboradores" rota="/tw/colaboradores" icon={<Engineering color="secondary" />} label="Colaboradores" drawerOpen={open} />
          <ProtectedMenuItem tela="ambiente-interno" rota="/tw/ambiente-interno" icon={<TroubleshootIcon color='secondary' />} label="Ambiente Interno" drawerOpen={open} />
          <ProtectedMenuItem tela="ambiente-externo" rota="/tw/ambiente-externo" icon={<DataUsageIcon color='secondary' />} label="Ambiente Externo" drawerOpen={open} />
          <ProtectedMenuItem tela="swot" rota="/tw/swot" icon={<FindInPage color='secondary' />} label="SWOT" drawerOpen={open} />
          <ProtectedMenuItem tela="meta-global" rota="/tw/metas-globais" icon={<UpdateIcon color='secondary' />} label="Metas Globais" drawerOpen={open} />
          <ProtectedMenuItem tela="metas" rota="/tw/metas" icon={<BarChartIcon color='secondary' />} label="Metas Específicas" drawerOpen={open} />
          <ProtectedMenuItem tela="plano-de-acao" rota="/tw/plano-de-acao" icon={<AssignmentTurnedInIcon color='secondary' />} label="Plano de Ação" drawerOpen={open} />
          <ProtectedMenuItem tela="desdobramento" rota="/tw/desdobramento" icon={<InsightsIcon color='secondary' />} label="Tarefas" drawerOpen={open} />

          {/* <ListItem component={Link} to="/tw/notificacoes" sx={activeStyle('/tw/notificacoes')}>
            <ListItemIcon>
              <NotificationsIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Notificações" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem> */}

          {/* <ListItem component={Link} to="/tw/avaliacao" sx={activeStyle('/tw/avaliacao')}>
            <ListItemIcon>
              <BarChartIcon color="secondary" />
            </ListItemIcon>
            <ListItemText secondary="Avaliação" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem> */}

          {/* <ListItem component={Link} to="/tw/relatorios" sx={activeStyle('/tw/relatorios')}>
            <ListItemIcon>
              <DescriptionIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Relatórios" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem> */}

          {/* Items de administração com proteção específica */}
          <ProtectedMenuItem tela="configuracoes-usuario" rota="/tw/configuracoes" icon={<SettingsIcon color='secondary' />} label="Configurações" drawerOpen={open} />

          {/* Relatório de Alterações — exclusivo do dono da empresa (OWNER) */}
          {user?.role?.toLocaleLowerCase() === UsuarioPerfil.OWNER.toLocaleLowerCase() && (
            <ListItem component={Link} to="/tw/auditoria" sx={{
              backgroundColor: location.pathname === '/tw/auditoria' ? '#EF453D20' : 'transparent',
              '&:hover': {
                backgroundColor: location.pathname === '/tw/auditoria' ? '#EF453D30' : '#EF453D10',
              },
              cursor: 'pointer',
              textDecoration: 'none',
            }}>
              <ListItemIcon>
                <HistoryIcon color='secondary' />
              </ListItemIcon>
              <ListItemText
                secondary="Relatório de Alterações"
                sx={{
                  textDecoration: 'none',
                  display: open ? 'block' : 'none'
                }}
              />
            </ListItem>
          )}

          {user?.admin === true && (
            <ListItem component={Link} to="/tw/admin" sx={{
              backgroundColor: location.pathname === '/tw/admin' ? '#EF453D20' : 'transparent',
              '&:hover': {
                backgroundColor: location.pathname === '/tw/admin' ? '#EF453D30' : '#EF453D10',
              },
              cursor: 'pointer',
              textDecoration: 'none',
            }}>
              <ListItemIcon>
                <ManageAccountsIcon color='secondary' />
              </ListItemIcon>
              <ListItemText
                secondary="Admin SaaS"
                sx={{
                  textDecoration: 'none',
                  display: open ? 'block' : 'none'
                }}
              />
            </ListItem>
          )}
        </List>
        <Box sx={{ mt: 'auto' }}>
          <List>
            <ListItem component={Link} onClick={handleLogout} to="/tw/login" sx={activeStyleLogout('/tw/login')}>
              <ListItemIcon>
                <LogoutIcon color='secondary' />
              </ListItemIcon>
              <ListItemText secondary="Logout" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Conteúdo principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 4 },
          mt: '64px',
          transition: 'margin-left 0.3s',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Outlet />
      </Box>
      <AiChatWidget />
    </Box>
  );
};

export default AppLayout;
